/**
 * Photo Cluster Engine
 * Multi-level, zoom-dependent clustering for photo markers on the map.
 */

import type { PhotoData } from '../../../../shared/types/project';

/** Zoom-dependent cluster level */
export enum ClusterLevel {
  /** Zoomed way out — everything nearby grouped into one neutral marker */
  AGGREGATE = 1,
  /** Mid zoom — nearby photos split by categoryId */
  CATEGORY = 2,
  /** Close zoom — ~1 m grouping (current behavior) */
  INDIVIDUAL = 3,
}

export interface PhotoCluster {
  /** Stable id for keying React / Cesium entities */
  id: string;
  level: ClusterLevel;
  center: { longitude: number; latitude: number };
  photos: PhotoData[];
  /** Set only at CATEGORY level — the shared categoryId */
  categoryId?: string;
}

/** Distance thresholds per level (in degrees, roughly) */
const THRESHOLDS: Record<ClusterLevel, number> = {
  [ClusterLevel.AGGREGATE]: 0.005,   // ~500 m
  [ClusterLevel.CATEGORY]: 0.0005,   // ~50 m
  [ClusterLevel.INDIVIDUAL]: 0.00001, // ~1 m
};

/**
 * Determine cluster level from camera height in meters.
 */
export function clusterLevelFromHeight(heightMeters: number): ClusterLevel {
  if (heightMeters > 5000) return ClusterLevel.AGGREGATE;
  if (heightMeters > 500) return ClusterLevel.CATEGORY;
  return ClusterLevel.INDIVIDUAL;
}

/**
 * Cluster photos based on the given level.
 * Uses the same greedy O(n²) grouping as the original `photoGroups` logic,
 * extended with a category-split step at CATEGORY level.
 */
export function clusterPhotos(photos: PhotoData[], level: ClusterLevel): PhotoCluster[] {
  if (photos.length === 0) return [];

  const threshold = THRESHOLDS[level];

  // --- Step 1: spatial grouping ------------------------------------------
  type SpatialGroup = {
    centerLng: number;
    centerLat: number;
    photos: PhotoData[];
  };
  const groups: SpatialGroup[] = [];

  for (const photo of photos) {
    const lng = photo.coordinates.longitude;
    const lat = photo.coordinates.latitude;

    let found = false;
    for (const g of groups) {
      if (
        Math.abs(lng - g.centerLng) < threshold &&
        Math.abs(lat - g.centerLat) < threshold
      ) {
        g.photos.push(photo);
        found = true;
        break;
      }
    }

    if (!found) {
      groups.push({ centerLng: lng, centerLat: lat, photos: [photo] });
    }
  }

  // --- Step 2: at CATEGORY level, split each spatial group by category ---
  if (level === ClusterLevel.CATEGORY) {
    const clusters: PhotoCluster[] = [];
    let idx = 0;
    for (const g of groups) {
      const byCategory = new Map<string, PhotoData[]>();
      for (const p of g.photos) {
        const key = p.categoryId;
        const arr = byCategory.get(key);
        if (arr) arr.push(p);
        else byCategory.set(key, [p]);
      }
      for (const [catId, catPhotos] of byCategory) {
        clusters.push({
          id: `cluster-cat-${idx++}`,
          level,
          center: { longitude: g.centerLng, latitude: g.centerLat },
          photos: catPhotos,
          categoryId: catId,
        });
      }
    }
    return clusters;
  }

  // --- AGGREGATE and INDIVIDUAL: one cluster per spatial group ------------
  return groups.map((g, i) => ({
    id: `cluster-${level}-${i}`,
    level,
    center: { longitude: g.centerLng, latitude: g.centerLat },
    photos: g.photos,
    categoryId: level === ClusterLevel.INDIVIDUAL ? g.photos[0].categoryId : undefined,
  }));
}
